// Aba SÉRIE — um seletor único para todas as séries da estação.
//
// Antes eram duas abas fixas: "Nível" (régua manual) e "Telemetria" (canais do
// modem), e o operador tinha que adivinhar em qual estava o dado. Estação mista
// — régua E telemetria ao mesmo tempo, que é o caso da CRVR — deixava a escolha
// artificial. Espelha o portal do cliente (monitoramento-frontend), que já
// resolvia assim; agora ERP e portal têm o mesmo modelo mental.
//
// As três origens do seletor:
//   • régua manual      → leituras_estacao   (origem 'manual')
//   • canais do modem   → leituras_telemetria (origem 'telemetria')
//   • hidrograma        → DERIVADO: cotagrama transformado pela curva-chave ativa
//
// Backend: `op_estacao_telemetria_vars` e `_serie` já aceitam `origem` — os dois
// usam os mesmos núcleos que o portal. Nada aqui é endpoint novo.
import { useEffect, useMemo, useState } from 'react';
import {
  App as AntApp, Button, Select, Space, Segmented, DatePicker, Spin, Empty,
  Typography, Alert, Tag,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import {
  Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
  ReferenceLine, Legend, Scatter, ComposedChart,
} from 'recharts';
import dayjs, { Dayjs } from 'dayjs';
import {
  dominioY, dominioVazao, dominioX, podarBuracos, limiarBuracoMs,
  grandezaDe, ehBarras, fmtValor, janelaAncorada, ancoraDefasada,
} from '../../utils/escalaGrafico';
import { api, nf, PRESETS, TELE_RES, TELE_AGG, aggDefault, fmtCadencia, Estacao } from './shared';

const { Text } = Typography;

type VarDisp = { variavel: string; n: number; n_consistido: number; de: string; ate: string; origem: string };
type Curva = { a: number; n: number; h0: number; r2: number | null; cota_min: number | null; cota_max: number | null; vigencia_inicio: string | null };

// Chave da opção: origem + variável. `hidrograma` é sintética (não existe tabela).
const chaveDe = (v: VarDisp) => `${v.origem}:${v.variavel}`;
const HIDROGRAMA = 'derivado:hidrograma';

export function TabSerie({ est, curva, medicoes, origem, alturaGrafico }: {
  est: Estacao;
  curva: Curva | null;
  medicoes: { id: string; t: number; vazao: number; cota: number | null }[];
  // MESMO componente nas abas Telemetria e Convencional — é o que garante que
  // os dois gráficos tenham escala, poda de buracos, unidade, janela ancorada e
  // tooltip idênticos. Só muda a tabela de origem dos dados.
  origem: 'telemetria' | 'manual';
  alturaGrafico?: string;
}) {
  const { message } = AntApp.useApp();
  const [vars, setVars] = useState<VarDisp[]>([]);
  const [sel, setSel] = useState<string>('');
  const [preset, setPreset] = useState<string>('30d');
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [resolucao, setResolucao] = useState<string>('auto');
  const [agregacao, setAgregacao] = useState<string>('media');
  const [serie, setSerie] = useState<{ lido_em: string; valor: number | null; valor_consistido: number | null; n: number }[]>([]);
  const [meta, setMeta] = useState<{ total: number; cadencia_seg: number | null; resolucao_seg: number | null; buckets: number;
    offset_info?: { aplicado: boolean; vigencias: number; atual: number | null } | null }>(
    { total: 0, cadencia_seg: null, resolucao_seg: null, buckets: 0 });
  // Eixo vertical do nível: 'local' = escala da régua (com offset sensor→régua
  // quando telemetria); 'altitude' soma o georref. vertical (cota_zero) — só
  // aparece quando a estação tem datum informado.
  const [eixoNivel, setEixoNivel] = useState<'local' | 'altitude'>('local');
  const [loadingVars, setLoadingVars] = useState(true);
  const [loading, setLoading] = useState(false);

  // ── Catálogo de séries: uma chamada por origem, como o portal faz ──────────
  useEffect(() => {
    (async () => {
      setLoadingVars(true);
      try {
        const r = await api('op_estacao_telemetria_vars', { id: est.id, origem }).catch(() => []);
        const todas: VarDisp[] = (Array.isArray(r) ? r : []).filter(v => v && v.variavel);
        setVars(todas);
        // Default: nível da régua > nível de telemetria > a série com mais dado.
        // Nunca abre numa série quase toda zero (chuva) nem na bateria.
        const prefere = todas.find(v => grandezaDe(v.variavel) === 'nivel')
          || [...todas].sort((a, b) => b.n - a.n)[0];
        if (prefere) {
          setSel(chaveDe(prefere));
          setAgregacao(aggDefault(prefere.variavel));
        } else if (curva) {
          setSel(HIDROGRAMA);
        }
      } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
      finally { setLoadingVars(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [est.id, origem]);

  // Opção selecionada resolvida (o hidrograma lê o cotagrama por baixo)
  const opcaoSel = useMemo(() => vars.find(v => chaveDe(v) === sel) || null, [vars, sel]);
  const vendoHidrograma = sel === HIDROGRAMA;
  // O hidrograma se alimenta da série de NÍVEL — de preferência a da régua.
  const fonteHidrograma = useMemo(() => (
    vars.find(v => grandezaDe(v.variavel) === 'nivel') || null
  ), [vars]);
  const efetiva = vendoHidrograma ? fonteHidrograma : opcaoSel;

  // Âncora da janela: última leitura DA SÉRIE escolhida (não "agora"). Régua
  // lida a cada 3-7 dias com "7d" contado de agora abre o gráfico vazio.
  const ancora = efetiva?.ate ?? null;
  const janela = useMemo(() => {
    if (range) return { de: range[0].startOf('day').toISOString(), ate: range[1].endOf('day').toISOString() };
    const p = PRESETS.find(x => x.label === preset);
    return janelaAncorada(p ? p.dias : 0, ancora);
  }, [preset, range, ancora]);

  async function carregar() {
    if (!efetiva) { setSerie([]); setMeta({ total: 0, cadencia_seg: null, resolucao_seg: null, buckets: 0 }); return; }
    setLoading(true);
    try {
      const r = await api('op_estacao_telemetria_serie', {
        id: est.id, variavel: efetiva.variavel, origem: efetiva.origem,
        de: janela.de, ate: janela.ate, resolucao, agregacao,
      });
      setSerie(Array.isArray(r?.pontos) ? r.pontos : []);
      setMeta({
        total: Number(r?.total) || 0, cadencia_seg: r?.cadencia_seg ?? null,
        resolucao_seg: r?.resolucao_seg ?? null, buckets: Number(r?.buckets) || 0,
        offset_info: r?.offset_info ?? null,
      });
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [est.id, sel, janela.de, janela.ate, resolucao, agregacao]);

  // ── Dados do gráfico ──────────────────────────────────────────────────────
  const brutos = useMemo(() => serie.map(l => ({
    t: dayjs(l.lido_em).valueOf(),
    valor: l.valor != null ? Number(l.valor) : null,
    consistido: l.valor_consistido != null ? Number(l.valor_consistido) : null,
  })), [serie]);

  // Poda das casas vazias da grade (só quando HÁ grade — a régua não é grade).
  const podados = useMemo(() => (
    meta.resolucao_seg ? podarBuracos(brutos, limiarBuracoMs(meta.resolucao_seg)) : brutos
  ), [brutos, meta.resolucao_seg]);

  // Hidrograma: Q = a·(h − h0)^n no cliente. h ≤ h0 → Q = 0 (calha sem
  // escoamento), não "dado ausente". Antes da vigência a curva não vale.
  const dados = useMemo(() => {
    if (!vendoHidrograma || !curva) return podados;
    const desde = curva.vigencia_inicio ? dayjs(curva.vigencia_inicio).valueOf() : null;
    const f = (h: number | null) => (h == null || !Number.isFinite(h)) ? null
      : (h <= curva.h0 ? 0 : curva.a * Math.pow(h - curva.h0, curva.n));
    return podados.map(d => (desde != null && d.t < desde)
      ? { ...d, valor: null, consistido: null }
      : { ...d, valor: f(d.valor), consistido: f(d.consistido) });
  }, [podados, vendoHidrograma, curva]);

  const variavelRotulo = vendoHidrograma ? 'Vazao' : (efetiva?.variavel || '');
  const grandeza = vendoHidrograma ? 'vazao' : grandezaDe(variavelRotulo);
  const barras = !vendoHidrograma && ehBarras(variavelRotulo);

  // Eixo do nível: 'local' plota como veio (telemetria já chega em cota local
  // quando há offset sensor→régua; régua É cota local). 'altitude' soma o
  // georref. vertical (cota_zero) — transformação constante, feita aqui.
  const cz = est.cota_zero != null ? Number(est.cota_zero) : null;
  const emAltitude = !vendoHidrograma && grandeza === 'nivel' && cz != null && eixoNivel === 'altitude';
  const dadosPlot = useMemo(() => (
    emAltitude
      ? dados.map(d => ({ ...d,
          valor: d.valor != null ? d.valor + cz! : null,
          consistido: d.consistido != null ? d.consistido + cz! : null }))
      : dados
  ), [dados, emAltitude, cz]);

  const temConsistido = useMemo(() => dadosPlot.some(d => d.consistido != null), [dadosPlot]);
  const pontosReais = useMemo(() => dadosPlot.reduce(
    (n, p) => n + (p.valor != null || p.consistido != null ? 1 : 0), 0), [dadosPlot]);
  const pontuada = pontosReais > 0 && pontosReais <= 150;

  // Medições de campo entram como Scatter SÓ no hidrograma — ali dividem o eixo
  // (m³/s). Sobre cotagrama seriam grandezas diferentes no mesmo eixo.
  const medNaJanela = useMemo(() => {
    if (!vendoHidrograma) return [];
    const de = janela.de ? dayjs(janela.de).valueOf() : -Infinity;
    const ate = janela.ate ? dayjs(janela.ate).valueOf() : Infinity;
    return medicoes.filter(m => m.t >= de && m.t <= ate);
  }, [vendoHidrograma, medicoes, janela.de, janela.ate]);

  // Domínios explícitos: com <Scatter data={...}> o recharts dimensiona os eixos
  // só pela série do Scatter e decapita os picos do hidrograma.
  const dominio = useMemo(() => (vendoHidrograma
    ? dominioVazao([...dadosPlot.flatMap(d => [d.valor, d.consistido]), ...medNaJanela.map(m => m.vazao)])
    : dominioY(dadosPlot.flatMap(d => [d.valor, d.consistido]), grandeza, barras)
  ), [vendoHidrograma, dadosPlot, medNaJanela, grandeza, barras]);
  const domX = useMemo(() => (medNaJanela.length ? dominioX(dadosPlot, medNaJanela) : ['dataMin', 'dataMax'] as any),
    [dadosPlot, medNaJanela]);

  const unidadeVista = vendoHidrograma ? 'm³/s' : (grandeza === 'nivel' ? est.unidade : '');

  // ── Opções do seletor ─────────────────────────────────────────────────────
  const opcoes = useMemo(() => {
    // Separa o que o cliente vê do que é saúde do equipamento. Não é só
    // arrumação: o portal do cliente ESCONDE as de diagnóstico, então o grupo
    // também responde "o que ele enxerga?" sem precisar abrir o portal.
    const rotulo = (v: any) => `${v.variavel} (${v.n.toLocaleString('pt-BR')})`;
    const medidas = vars.filter((v: any) => !v.diagnostico).map(v => ({ value: chaveDe(v), label: rotulo(v) }));
    const diag = vars.filter((v: any) => v.diagnostico).map(v => ({ value: chaveDe(v), label: rotulo(v) }));
    const grupos: any[] = [];
    if (medidas.length) grupos.push({ label: 'Medido', options: medidas });
    if (diag.length) grupos.push({ label: 'Diagnóstico do equipamento (não vai para o cliente)', options: diag });
    if (curva && fonteHidrograma) {
      grupos.push({ label: 'Derivada', options: [{ value: HIDROGRAMA, label: 'Vazão · hidrograma (curva-chave)' }] });
    }
    return grupos;
  }, [vars, curva, fonteHidrograma]);

  function baixarCsv() {
    if (!efetiva) return;
    const cab = ['lido_em', 'variavel', 'origem', 'valor', 'valor_consistido', 'n'];
    const fmtN = (v: any) => (v == null || v === '') ? '' : String(+Number(v).toFixed(6)).replace('.', ',');
    const linhas = serie.map((l: any) => [
      dayjs(l.lido_em).format('YYYY-MM-DD HH:mm:ss'), efetiva.variavel, efetiva.origem,
      fmtN(l.valor), fmtN(l.valor_consistido), l.n ?? '',
    ].join(';'));
    const csv = '﻿' + [cab.join(';'), ...linhas].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${est.codigo}-${efetiva.variavel}-${efetiva.origem}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  if (loadingVars) return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  if (!vars.length && !curva) return <Empty description={origem === 'manual' ? 'Sem leitura de régua registrada' : 'Sem dado de telemetria'} style={{ padding: 40 }} />;

  // Resolução/agregação só fazem sentido em grade — a régua é ponto medido.
  const temGrade = origem === 'telemetria';

  return (
    <>
      <Space style={{ marginBottom: 6 }} wrap size={[8, 6]}>
        <Select size="small" value={sel || undefined} onChange={(v) => {
          setSel(v);
          const o = vars.find(x => chaveDe(x) === v);
          if (o) setAgregacao(aggDefault(o.variavel));
        }} style={{ minWidth: 300 }} options={opcoes} placeholder="Escolha a série"
          showSearch optionFilterProp="label" />
        <Segmented size="small" value={range ? '' : preset} options={PRESETS.map(p => p.label)}
          onChange={(v) => { setRange(null); setPreset(String(v)); }} />
        <DatePicker.RangePicker size="small" value={range as any} onChange={(v) => setRange(v as any)} allowClear />
        {temGrade && (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>Resolução</Text>
            <Select size="small" value={resolucao} onChange={setResolucao} style={{ width: 92 }} options={TELE_RES}
              showSearch optionFilterProp="label" />
            <Text type="secondary" style={{ fontSize: 12 }}>Agregação</Text>
            <Select size="small" value={agregacao} onChange={setAgregacao} style={{ width: 108 }} options={TELE_AGG}
              showSearch optionFilterProp="label" />
          </>
        )}
        <Button size="small" icon={<DownloadOutlined />} onClick={baixarCsv} disabled={!serie.length}>CSV</Button>
      </Space>

      {vendoHidrograma && curva && (
        <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>
          <Tag color="blue" style={{ marginRight: 6 }}>derivado</Tag>
          Q = {nf(curva.a, 4)}·(h − {nf(curva.h0, 3)})<sup>{nf(curva.n, 3)}</sup>
          {curva.r2 != null ? ` · R² ${nf(curva.r2, 4)}` : ' · R² não informado'}
          {curva.vigencia_inicio && ` · vigente desde ${dayjs(curva.vigencia_inicio).format('DD/MM/YYYY')}`}
          {fonteHidrograma && ` · sobre ${fonteHidrograma.variavel} (${fonteHidrograma.origem === 'manual' ? 'régua' : 'telemetria'})`}
        </div>
      )}

      {!vendoHidrograma && grandeza === 'nivel' && (meta.offset_info || cz != null) && (
        <div style={{ marginBottom: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {meta.offset_info && (
            <Tag color="geekblue" style={{ marginRight: 0 }}
              title={`Offset sensor→régua aplicado leitura a leitura (${meta.offset_info.vigencias} vigência${meta.offset_info.vigencias > 1 ? 's' : ''}${meta.offset_info.atual != null ? ` · atual ${nf(meta.offset_info.atual, 3)} ${est.unidade}` : ''}). A série está na escala da régua.`}>
              cota local (offset sensor→régua)
            </Tag>
          )}
          {cz != null && (
            <Segmented size="small" value={eixoNivel}
              options={[{ label: 'Cota local', value: 'local' },
                        { label: `Altitude (+${nf(cz, 3)} ${est.unidade})`, value: 'altitude' }]}
              onChange={(v) => setEixoNivel(v as any)} />
          )}
        </div>
      )}

      <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>
        {meta.total.toLocaleString('pt-BR')} pts · cadência ~{fmtCadencia(meta.cadencia_seg)}
        {meta.resolucao_seg ? ` · grade ${fmtCadencia(meta.resolucao_seg)} (${meta.buckets.toLocaleString('pt-BR')} buckets)` : ' · pontos medidos'}
        {' · '}{pontosReais.toLocaleString('pt-BR')} no gráfico
      </div>

      {!range && ancoraDefasada(ancora) && (
        <Alert type="warning" showIcon style={{ marginBottom: 6 }} banner
          message={`Período ancorado na última leitura (${dayjs(ancora).format('DD/MM/YYYY HH:mm')}) — sem dado novo desde então`} />
      )}

      <div style={{ height: alturaGrafico || 'clamp(300px, calc(100vh - 290px), 1100px)', marginBottom: 8 }}>
        {loading ? <div style={{ textAlign: 'center', padding: 90 }}><Spin /></div> :
         dadosPlot.length === 0 ? <Empty description="Sem dados nesta janela" style={{ paddingTop: 80 }} /> : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dadosPlot} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="t" type="number" scale="time" domain={domX}
                tickFormatter={(v: number) => dayjs(v).format('DD/MM HH:mm')} tick={{ fontSize: 10 }} minTickGap={40} />
              <YAxis tick={{ fontSize: 10 }} domain={dominio} />
              <RTooltip content={({ active, payload, label }: any) => {
                if (!active || !payload?.length) return null;
                const t = Number(label);
                const linha = payload.find((p: any) => p?.payload?.t === t && p.value != null && p.dataKey !== 'vazao');
                const med = medNaJanela.find(m => m.t === t);
                return (
                  <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 4, padding: '6px 9px', fontSize: 12 }}>
                    <div style={{ color: '#888', marginBottom: 2 }}>{dayjs(t).format('DD/MM/YY HH:mm')}</div>
                    {linha && <div><b>{fmtValor(linha.value, variavelRotulo, est.unidade)}</b></div>}
                    {med && <div style={{ color: '#0930D0' }}>medição: <b>{med.vazao.toFixed(3)}</b> m³/s</div>}
                  </div>
                );
              }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {/* Faixa de atenção só faz sentido no NÍVEL (mesma grandeza da régua).
                  faixa = 0 vindo de import é extensão física, não cota de alerta. */}
              {grandeza === 'nivel' && !vendoHidrograma && est.faixa_min != null && Number(est.faixa_min) !== 0 &&
                <ReferenceLine y={Number(est.faixa_min)} stroke="#f59e0b" strokeDasharray="4 4" />}
              {grandeza === 'nivel' && !vendoHidrograma && est.faixa_max != null && Number(est.faixa_max) !== 0 &&
                <ReferenceLine y={Number(est.faixa_max)} stroke="#f59e0b" strokeDasharray="4 4" />}
              {barras ? (
                <>
                  <Bar dataKey="valor" name="Bruto" fill={temConsistido ? '#b0b8cc' : '#0930D0'} isAnimationActive={false} />
                  {temConsistido && <Bar dataKey="consistido" name="Consistido" fill="#0a7f4a" isAnimationActive={false} />}
                </>
              ) : (
                <>
                  <Line type="monotone" dataKey="valor" name="Bruto" stroke={temConsistido ? '#b0b8cc' : '#0930D0'}
                    dot={pontuada ? { r: 2 } : false} strokeWidth={temConsistido ? 1.3 : 1.8} isAnimationActive={false} connectNulls={false} />
                  {temConsistido && <Line type="monotone" dataKey="consistido" name="Consistido" stroke="#0a7f4a"
                    dot={pontuada ? { r: 2 } : false} strokeWidth={1.8} isAnimationActive={false} connectNulls={false} />}
                </>
              )}
              {medNaJanela.length > 0 && (
                <Scatter name="Medições de campo" data={medNaJanela} dataKey="vazao" fill="#0930D0" shape="circle" isAnimationActive={false} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      {unidadeVista && (
        <div style={{ fontSize: 11, color: '#999', marginTop: -8 }}>Eixo em {unidadeVista}</div>
      )}
    </>
  );
}
